import {ReactElement, useCallback, useRef, useState} from 'react';
import {Swiper, SwiperSlide} from '../../swiper';
import ProductReviewTile from '../ProductReviewTile.client';

import {useModalToggle} from '../../hooks';
import type {ProductReview} from '../../types';
import ProductReviewModal from '../modals/ProductReviewModal.client';

interface UserReviewCarouselPaginatedProps {
  reviews: ReadonlyArray<ProductReview>;
  prevUrl?: string;
  nextUrl?: string;
  setCurrentPage?: (page) => void;
  showImage?: boolean;
  swiperBreakpointsConfig: {
    [key: number]: {
      slidesPerView: number;
      spaceBetween?: number;
    };
  };
}

export default function UserReviewCarouselPaginated({
  reviews,
  prevUrl,
  nextUrl,
  setCurrentPage,
  swiperBreakpointsConfig,
  showImage = true,
}: UserReviewCarouselPaginatedProps): ReactElement {
  const {isOpen, openModal, closeModal} = useModalToggle('reviewProduct');
  const [selectedReview, setSelectedReview] = useState<ProductReview>(null);

  const prevNavRef = useRef<HTMLDivElement>(null);
  const nextNavRef = useRef<HTMLDivElement>(null);

  const handleModalOpen = useCallback(
    (review) => {
      setSelectedReview(review);
      openModal();
    },
    [openModal],
  );

  const setPageValue = useCallback(
    (fullUrl: string) => {
      if (!fullUrl) return;
      const url = new URL(fullUrl);
      const urlParams = new URLSearchParams(url.search);
      const page = urlParams.get('page') || 1; // default to ?page=1
      if (page) setCurrentPage(page);
    },
    [setCurrentPage],
  );

  return (
    <div className="reviews__list carousel-outer">
      {(prevUrl || nextUrl) && (
        <div className="carousel-controls">
          {prevUrl && (
            <div
              className="carousel-control carousel-control-left"
              role="button"
              tabIndex={0}
              ref={prevNavRef}
              onClick={() => setPageValue(prevUrl)}
            >
              <span className="icon-arrow-left" />
            </div>
          )}
          {nextUrl && (
            <div
              className="carousel-control carousel-control-right"
              role="button"
              tabIndex={0}
              ref={nextNavRef}
              onClick={() => setPageValue(nextUrl)}
            >
              <span className="icon-arrow-right" />
            </div>
          )}
        </div>
      )}

      <Swiper
        id="reviews-carousel"
        className="reviews__carousel"
        slidesPerView={1}
        spaceBetween={20}
        breakpoints={swiperBreakpointsConfig}
        navigation={{
          prevEl: prevNavRef.current,
          nextEl: nextNavRef.current,
        }}
        onBeforeInit={({params: {navigation}}) => {
          if (navigation && typeof navigation !== 'boolean') {
            navigation.prevEl = prevNavRef.current;
            navigation.nextEl = nextNavRef.current;
          }
        }}
        onTransitionEnd={(swiper) => {
          const {isEnd, swipeDirection} = swiper as any; // eslint-disable-line @typescript-eslint/no-explicit-any
          if (isEnd) {
            if (swipeDirection === 'prev') {
              setPageValue(prevUrl);
            } else if (swipeDirection === 'next') {
              setPageValue(nextUrl);
            }
          }
        }}
      >
        {reviews.map((review) => (
          <SwiperSlide key={review.uuid} className="reviews__item">
            <ProductReviewTile
              review={review}
              showImage={showImage}
              onReviewOpen={() => handleModalOpen(review)}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <ProductReviewModal
        isOpen={isOpen && Boolean(selectedReview)}
        productReview={selectedReview}
        onRequestClose={closeModal}
      />
    </div>
  );
}
